import React, { useEffect, useState } from "react";

const prompts = [
  {
    icon: "📊",
    text: "Show me volunteer impact by city last month",
    tooltip:
      "See a breakdown of volunteer hours and participation by city for the previous month.",
  },
  {
    icon: "🧾",
    text: "Generate an impact report for Earth Day",
    tooltip:
      "Get a ready-to-use summary of your organization’s Earth Day activities and outcomes.",
  },
  {
    icon: "🔍",
    text: "Find NGOs aligned with Skill Development in Mumbai",
    tooltip: "Discover NGOs working in skill development in the Mumbai region.",
  },
  {
    icon: "📆",
    text: "What’s trending on UN Observance Days next month?",
    tooltip:
      "View upcoming global observance days and related trending causes.",
  },
];

export const SuggestedPromptsBar: React.FC<{
  onPromptClick?: (text: string) => void;
  autoSubmit?: boolean;
}> = ({ onPromptClick, autoSubmit = false }) => {
  const prompts = [
    {
      icon: "📊",
      text: "CSR impact YTD summary",
      tooltip: "Dashboard of all key impact metrics for the current fiscal.",
    },
    {
      icon: "⚖️",
      text: "ESG score vs last quarter",
      tooltip: "Trend line of your composite ESG rating.",
    },
    {
      icon: "📊",
      text: "Volunteer hours MoM trend",
      tooltip: "Month-over-month growth in volunteering.",
    },
    {
      icon: "🗺️",
      text: "Heat-map: impact by city",
      tooltip: "Geographic breakdown of beneficiaries reached.",
    },
    {
      icon: "🌍",
      text: "Carbon footprint vs 2024 baseline",
      tooltip: "Absolute & intensity metrics, Scope-1/2/3.",
    },
    {
      icon: "🏅",
      text: "Top 5 causes by ROI",
      tooltip: "Highest impact per dollar donated.",
    },
    {
      icon: "📈",
      text: "Benchmark CSR against Fortune 500",
      tooltip: "Percentile ranking on spend, hours & SDGs.",
    },
    {
      icon: "📉",
      text: "Volunteering hours vs IT-services median",
      tooltip: "Compare engagement to sector peers.",
    },
    {
      icon: "🔍",
      text: "Peer list: companies with 30%+ participation",
      tooltip: "See programmes setting the bar.",
    },
    {
      icon: "🥇",
      text: "Emerging ESG leaders in APAC",
      tooltip: "Companies moving fastest on ESG adoption.",
    },
    {
      icon: "🤝",
      text: "Find NGOs aligned with SDG 4 in Karnataka",
      tooltip: "Curated list with due-diligence scores.",
    },
    {
      icon: "🏆",
      text: "Top-performing NGO this month",
      tooltip: "Highest engagement + beneficiary impact.",
    },
    {
      icon: "🕵️",
      text: "NGO risk red-flags (financial, legal)",
      tooltip: "Screen partners for compliance issues.",
    },
    {
      icon: "🗄️",
      text: "Download CSR-1 / 80G certificates of all partners",
      tooltip: "Instant compliance packet.",
    },
    {
      icon: "💡",
      text: "3 high-ROI ESG initiatives for manufacturing",
      tooltip: "Data-backed programme ideas.",
    },
    {
      icon: "🧭",
      text: "Roadmap to achieve BRSR ‘Lead’ rating",
      tooltip: "Step-by-step readiness checklist.",
    },
    {
      icon: "📦",
      text: "Quarterly cause-focus recommendation",
      tooltip: "Causes forecasted to resonate this Q.",
    },
    {
      icon: "🧩",
      text: "Volunteer portfolio optimiser (suggest mix)",
      tooltip: "Best mix given budget & hour goals.",
    },
    {
      icon: "🚀",
      text: "Boost employee volunteering to 35%",
      tooltip: "Personalised nudges, comms & leave policy tips.",
    },
    {
      icon: "📋",
      text: "Volunteer hours by department",
      tooltip: "Engagement leaderboard across units.",
    },
    {
      icon: "🎉",
      text: "Design a company-wide Day of Service",
      tooltip: "Blueprint inc. logistics & sample comms.",
    },
    {
      icon: "🎯",
      text: "Gamification ideas for remote teams",
      tooltip: "Badges, leaderboards & micro-challenges.",
    },
    {
      icon: "🌐",
      text: "Global observance days in next 60 days",
      tooltip: "Events to rally employees around.",
    },
    {
      icon: "🧾",
      text: "Generate ESG report draft for Q2",
      tooltip: "Auto-filled narrative + charts.",
    },
    {
      icon: "📜",
      text: "One-page impact brief for C-Suite",
      tooltip: "Executive-ready snapshot.",
    },
    {
      icon: "✉️",
      text: "Draft employee newsletter on CSR wins",
      tooltip: "Celebratory internal comms.",
    },
    {
      icon: "🎙️",
      text: "Talking points for annual town-hall",
      tooltip: "Data-driven CSR highlights & stories.",
    },
    {
      icon: "🔐",
      text: "BRSR compliance checklist status",
      tooltip: "Track each principle’s evidence & gaps.",
    },
    {
      icon: "📑",
      text: "Pending audits & statutory filings",
      tooltip: "Deadlines and responsible owners.",
    },
    {
      icon: "🚨",
      text: "Flag donations breaching 2% CSR rule",
      tooltip: "Alert for overspend/underspend.",
    },
    {
      icon: "🗓️",
      text: "Schedule auto-send of monthly impact reports",
      tooltip: "Automate PDF/Excel distribution.",
    },
    {
      icon: "✅",
      text: "Close open action items from last audit",
      tooltip: "Task list & status update.",
    },
    {
      icon: "🔄",
      text: "Sync volunteering events to Outlook",
      tooltip: "Push bookings to employee calendars.",
    },
    {
      icon: "📊",
      text: "Predict attrition reduction from volunteering",
      tooltip: "Correlate engagement with HR churn.",
    },
    {
      icon: "🤖",
      text: "AI forecast: volunteer hours next quarter",
      tooltip: "Regression based on seasonality + org growth.",
    },
    {
      icon: "🌡️",
      text: "Sentiment analysis on volunteer feedback",
      tooltip: "Word cloud & NPS scoring.",
    },
    {
      icon: "🌱",
      text: "Estimate CO₂ offset from tree-planting events",
      tooltip: "Kg/tonnes stored annually.",
    },
    {
      icon: "🚗",
      text: "Convert commuting miles to CO₂ metrics",
      tooltip: "Scope-3 calculator.",
    },
    {
      icon: "♻️",
      text: "Plastic-free campaign impact projection",
      tooltip: "Waste diverted & emission savings.",
    },
    {
      icon: "👩‍💻",
      text: "Match data-science volunteers to NGOs",
      tooltip: "Skills inventory vs NGO needs.",
    },
    {
      icon: "🎯",
      text: "Quantify in-kind hours value (₹/hr)",
      tooltip: "Monetise skilled volunteering.",
    },
    {
      icon: "📚",
      text: "Catalogue pro-bono case studies",
      tooltip: "Success stories for reference.",
    },
    {
      icon: "🎲",
      text: "Random CSR idea generator",
      tooltip: "Spin to discover fresh micro-campaigns.",
    },
    {
      icon: "🗣️",
      text: "Translate impact summary into Hindi",
      tooltip: "Multi-lingual comms.",
    },
    {
      icon: "🏝️",
      text: "CSR offsite retreat agenda",
      tooltip: "Mix of volunteering & strategy sessions.",
    },
    {
      icon: "📅",
      text: "Create 90-day CSR content calendar",
      tooltip: "Social media + intranet posts.",
    },
    // Removed duplicate/legacy prompts below. If you want to keep them, add them above with a comma.

    {
      icon: "🌍",
      text: "How does our environmental impact compare to last year?",
      tooltip: "Year-over-year environmental metrics.",
    },
    {
      icon: "📢",
      text: "Share recent CSR success stories from our industry",
      tooltip: "Get inspired by what others are doing.",
    },
    {
      icon: "🕒",
      text: "What are the latest trends in corporate volunteering?",
      tooltip: "Stay current with volunteering trends.",
    },
    {
      icon: "📑",
      text: "Generate a peer benchmarking report for ESG",
      tooltip: "Compare your ESG scores to industry peers.",
    },
    {
      icon: "📦",
      text: "What are the most impactful causes this quarter?",
      tooltip: "Prioritize causes that drive the most impact.",
    },
    {
      icon: "🧑‍🤝‍🧑",
      text: "How can we partner with more NGOs?",
      tooltip: "Find and connect with new NGO partners.",
    },
    {
      icon: "📦",
      text: "Show our company’s SDG alignment",
      tooltip: "See which Sustainable Development Goals your work supports.",
    },
    {
      icon: "📬",
      text: "Draft an internal newsletter about our CSR progress",
      tooltip: "Communicate impact to employees.",
    },
  ];

  // Show 3 random prompts at a time
  const [currentIndices, setCurrentIndices] = useState<number[]>(() => {
    const idxs = new Set<number>();
    while (idxs.size < 3 && idxs.size < prompts.length) {
      idxs.add(Math.floor(Math.random() * prompts.length));
    }
    return Array.from(idxs);
  });

  // Rotate to 3 new random prompts every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndices(() => {
        const idxs = new Set<number>();
        while (idxs.size < 3 && idxs.size < prompts.length) {
          idxs.add(Math.floor(Math.random() * prompts.length));
        }
        return Array.from(idxs);
      });
    }, 8000);
    return () => clearInterval(interval);
  }, [prompts.length]);

  const handleShowAnother = () => {
    setCurrentIndices(() => {
      const idxs = new Set<number>();
      while (idxs.size < 3 && idxs.size < prompts.length) {
        idxs.add(Math.floor(Math.random() * prompts.length));
      }
      return Array.from(idxs);
    });
  };

  return (
    <div className="flex items-center justify-between gap-2 mb-2 w-full px-2 py-1 bg-white/80 rounded-lg border border-green-100 shadow-sm">
      <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar flex-grow">
        {currentIndices.map((idx) => {
          const prompt = prompts[idx];
          return (
            <button
              key={prompt.text}
              className="flex-shrink-0 flex items-center gap-1 px-3 py-1 rounded-full border border-green-200 bg-white hover:bg-green-50 transition group relative focus:outline-none focus:ring-1 focus:ring-green-400 text-green-900 text-xs whitespace-nowrap"
              onClick={() => {
                if (onPromptClick) {
                  onPromptClick(prompt.text);
                  // Add a small delay before submitting to ensure the input is updated
                  if (autoSubmit) {
                    setTimeout(() => {
                      // Find and click the submit button
                      const submitButton = document.querySelector(
                        'form button[type="submit"]'
                      );
                      if (submitButton instanceof HTMLButtonElement) {
                        submitButton.click();
                      }
                    }, 50);
                  }
                }
              }}
              type="button"
            >
              <span className="text-sm">{prompt.icon}</span>
              <span>{prompt.text}</span>
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs bg-black/90 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-20 whitespace-pre-line shadow-lg">
                {prompt.tooltip}
              </span>
            </button>
          );
        })}
      </div>
      <button
        className="flex-shrink-0 px-3 py-1 rounded-full bg-gradient-to-tr from-green-100 to-blue-100 text-green-800 text-xs font-semibold hover:bg-blue-50 transition"
        onClick={handleShowAnother}
        type="button"
      >
        More
      </button>
    </div>
  );
};

export default SuggestedPromptsBar;

// Add CSS for hiding scrollbar while allowing scrolling
const style = document.createElement("style");
style.textContent = `
  .hide-scrollbar {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
  .hide-scrollbar::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;
document.head.appendChild(style);
